/**
 * Paraméterek — handover rev7 §11.7 Settings.
 *
 * **B2b of the catalogue brief of 19 October 2026** opens four of them to an
 * admin: `email_allowed_recipients` and the three catalogue-sync timings.
 * Not a general editor — which parameters a screen may move is a decision
 * §12 has not taken — so the writable ones come back from the endpoint, from
 * `public.console_params_writable()`, and everything else still reads.
 * `public.params` bumps the ruleset version on every update through its own
 * trigger, so a change made here is versioned exactly as a migration's is,
 * and the next email obeys it with no deploy and no restart.
 *
 * rev7 makes every change to a parameter a new `ruleset_version`, and a
 * half-built settings screen that changed a value without versioning it would
 * be worse than none at all — so this one reads and says so. 030 named
 * `041_staff_editor` as the stage that would write it; that stage is §11.11
 * and §11.8 and does not touch §12, so the screen is still read-only and no
 * stage in `build/QUEUE.md` claims it. Named as an open item for Barna
 * instead of pointing at a stage that has been and gone.
 *
 * Each row carries the register's own **Basis** column (§12) — DECIDED,
 * PROPOSED, EXPERT or a guess with its backlog number — because the whole
 * point of that column is that nothing reads as settled that is not.
 */

import { useState } from 'react'

import { asMoment, fmt, Loading, useConsoleAction, useConsoleData } from '../common.tsx'

interface Settings {
  ruleset_version: number
  catalogue_version: string | null
  app_env: string
  recipients: {
    role: string
    address: string | null
    label: string | null
    active: boolean
    note: string | null
  }[]
  params: {
    key: string
    value: string | null
    unit: string | null
    status: string | null
    ruleset_version: number
    updated_at: string
  }[]
  /** B2b: the keys this screen may change. Absent on an older endpoint, and
   *  then nothing is editable, which is the safe way round. */
  writable_params?: string[]
}

export function Settings(): JSX.Element {
  const { data, error, loading, reload } = useConsoleData<Settings>('settings', {})
  const { run, busy, error: writeError } = useConsoleAction()
  const [filter, setFilter] = useState('')
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState('')

  const writable = new Set(data?.writable_params ?? [])
  const save = async (key: string): Promise<void> => {
    if (await run('param_set', { key, value: draft })) {
      setEditing(null)
      reload()
    }
  }

  const params = (data?.params ?? []).filter(
    (p) => filter === '' || p.key.toLowerCase().includes(filter.toLowerCase()),
  )

  return (
    <section data-testid="view-settings">
      <h2>Paraméterek</h2>
      <p className="lead">
        §12 paraméterregiszter és §11.5 címzettek. A legtöbb érték csak olvasható; néhány
        paramétert adminisztrátor itt is módosíthat. Minden módosítás új szabálykészlet-verziót
        hoz létre, és a következő levélre, illetve a következő szinkronra már az új érték
        érvényes — telepítés nélkül.
      </p>

      <Loading loading={loading} error={error} />

      {data !== null && (
        <>
          <div className="card">
            <dl className="facts">
              <dt>Szabálykészlet</dt>
              <dd data-testid="ruleset-version">{fmt.n(data.ruleset_version)}</dd>
              <dt>Katalógus</dt>
              <dd>{data.catalogue_version ?? '— nincs publikálva'}</dd>
              <dt>Környezet</dt>
              <dd data-testid="settings-env">{data.app_env}</dd>
            </dl>
          </div>

          <article className="card">
            <h3>Címzettek (§11.5)</h3>
            <table data-testid="recipients">
              <thead>
                <tr>
                  <th>Szerep</th>
                  <th>Cím</th>
                  <th>Megjegyzés</th>
                </tr>
              </thead>
              <tbody>
                {data.recipients.map((recipient) => (
                  <tr key={recipient.role}>
                    <td>{recipient.label ?? recipient.role}</td>
                    <td>
                      {recipient.address ?? (
                        <span className="tag warn">nincs cím — hiányzó adat</span>
                      )}
                    </td>
                    <td>{recipient.note ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </article>

          <article className="card">
            <h3>Paraméterek (§12)</h3>
            <label htmlFor="param-filter">Szűrés kulcsra</label>
            <input
              id="param-filter"
              type="text"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              data-testid="param-filter"
            />
            <table data-testid="params">
              <thead>
                <tr>
                  <th>Kulcs</th>
                  <th>Érték</th>
                  <th>Egység</th>
                  <th>Alap (Basis)</th>
                  <th>Módosítva</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {params.map((param) => (
                  <tr key={param.key} data-testid={`param-${param.key}`}>
                    <td><code>{param.key}</code></td>
                    <td>
                      {editing === param.key ? (
                        <input
                          type="text"
                          value={draft}
                          onChange={(event) => setDraft(event.target.value)}
                          data-testid={`param-input-${param.key}`}
                          aria-label={`${param.key} értéke`}
                        />
                      ) : (
                        param.value ?? '—'
                      )}
                    </td>
                    <td>{param.unit ?? ''}</td>
                    <td>
                      <span
                        className={`tag${
                          (param.status ?? '').startsWith('DECIDED') ? ' ok' : ' warn'
                        }`}
                      >
                        {param.status ?? 'nincs megadva'}
                      </span>
                    </td>
                    <td>{asMoment(param.updated_at)}</td>
                    <td>
                      {!writable.has(param.key) ? null : editing === param.key ? (
                        <>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => void save(param.key)}
                            data-testid={`param-save-${param.key}`}
                          >
                            Mentés
                          </button>
                          <button type="button" onClick={() => setEditing(null)}>
                            Mégsem
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          data-testid={`param-edit-${param.key}`}
                          onClick={() => {
                            setEditing(param.key)
                            setDraft(param.value ?? '')
                          }}
                        >
                          Módosítás
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {writeError !== null && (
              <p className="tag stop" data-testid="param-error">{writeError}</p>
            )}
          </article>
        </>
      )}
    </section>
  )
}
